import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle2, Phone, MessageCircle, MessageSquare } from "lucide-react";
import heroPerson from "@/assets/hero-person.png";
import { useLanguage } from "@/hooks/useLanguage";

const HeroSection = () => {
  const { t, tArray, localizePath } = useLanguage();
  const features = tArray('hero.features');
  
  return (
    <section className="relative pt-36 md:pt-48 pb-8 md:pb-12 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-secondary/30" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold text-primary leading-tight mb-6 animate-fade-in lg:text-5xl">
              {t('hero.title')}
              <span className="block text-foreground">{t('hero.titleLine2')}</span>
            </h1>
            
            <p className="text-lg md:text-[1.35rem] text-chathero-orange font-semibold mb-4 animate-fade-in" style={{
              animationDelay: "0.1s"
            }}>
              {t('hero.subtitle')}<br />{t('hero.subtitleLine2')}
            </p>
            
            <p className="text-base md:text-lg text-muted-foreground mb-2 animate-fade-in" style={{
              animationDelay: "0.2s"
            }}>
              {t('hero.description')}
            </p>
            <p className="text-base md:text-lg text-muted-foreground mb-8 animate-fade-in" style={{
              animationDelay: "0.25s"
            }}>
              {t('hero.description2')}
            </p>

            {/* Feature list */}
            <ul className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <li 
                  key={index} 
                  className="flex items-center gap-3 text-foreground animate-slide-in-left" 
                  style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                >
                  <CheckCircle2 className="w-5 h-5 text-chathero-mint flex-shrink-0" />
                  <span className="font-medium">{feature}</span>
                </li>
              ))}
            </ul>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-8 animate-fade-in" style={{
              animationDelay: "0.7s"
            }}>
              <Button size="lg" className="bg-chathero-mint text-accent-foreground hover:bg-chathero-mint/90 text-base px-8" asChild>
                <a href="https://app.chathero.ai/register/essential" target="_blank" rel="noopener noreferrer">{t('hero.cta.primary')}</a>
              </Button>
              <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground text-base px-8" asChild>
                <Link to={localizePath("/preise")}>{t('hero.cta.secondary')}</Link>
              </Button>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-6 animate-fade-in" style={{
              animationDelay: "0.8s"
            }}>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4 text-chathero-mint" />
                </div>
                <span>{t('hero.trustBadges.gdpr')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4 text-chathero-mint" />
                </div>
                <span>{t('hero.trustBadges.euHosting')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4 text-chathero-mint" />
                </div>
                <span>{t('hero.trustBadges.ready')}</span>
              </div>
            </div>
          </div>

          {/* Right side - Hero Image with decorative elements */}
          <div className="relative hidden lg:block animate-scale-in" style={{
            animationDelay: "0.3s"
          }}>
            <div className="relative w-full max-w-lg mx-auto">
              {/* Decorative mint circle background */}
              <div className="absolute -top-8 -right-8 w-[450px] h-[450px] rounded-full bg-gradient-to-br from-chathero-mint to-chathero-mint/40 blur-sm" />
              
              {/* Person image */}
              <div className="relative z-10">
                <img src={heroPerson} alt="AI Agent Plattform Chathero" className="w-full h-auto rounded-3xl shadow-2xl" width="512" height="600" fetchPriority="high" loading="eager" />
              </div>
              
              {/* Floating channel icons */}
              <div className="absolute -top-4 -right-4 w-16 h-16 rounded-2xl bg-background shadow-xl flex items-center justify-center animate-bounce z-20" style={{
                animationDuration: "3s"
              }}>
                <Phone className="w-8 h-8 text-primary" />
              </div>
              <div className="absolute bottom-20 -right-8 w-16 h-16 rounded-2xl bg-background shadow-xl flex items-center justify-center animate-bounce z-20" style={{
                animationDuration: "3.5s",
                animationDelay: "0.5s"
              }}>
                <MessageCircle className="w-8 h-8 text-chathero-mint" />
              </div>
              <div className="absolute top-1/2 -left-8 w-16 h-16 rounded-2xl bg-background shadow-xl flex items-center justify-center animate-bounce z-20" style={{
                animationDuration: "4s",
                animationDelay: "1s"
              }}>
                <MessageSquare className="w-8 h-8 text-chathero-orange" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;